import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { api, unwrap, type Schemas } from '../api/client.js';
import { formatDate } from '../i18n.js';
import { CheckIcon } from './icons.js';
import { useLocale } from './site.js';

export type Improvement = Schemas['Improvement'];

/** What got better on a site in the last `days` days (T-36), newest first. */
export function useImprovements(siteId: string, days: number) {
  const locale = useLocale();
  return useQuery({
    queryKey: ['site', siteId, 'improvements', days, locale],
    queryFn: () =>
      unwrap(
        api.GET('/sites/{siteId}/improvements', {
          params: { path: { siteId }, query: { days, locale } },
        }),
      ),
  });
}

/** One piece of good news: what changed, before → after, and what it brings. */
export function ImprovementItem({
  item,
  timeZone,
}: {
  item: Improvement;
  timeZone?: string | undefined;
}) {
  const { t, i18n } = useTranslation();
  return (
    <article className="flex gap-3 rounded-md border border-green-200 bg-white p-4">
      <span
        aria-hidden="true"
        className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-green-100 text-green-800"
      >
        <CheckIcon />
      </span>
      <div className="flex min-w-0 flex-col gap-1">
        <h3 className="font-semibold">{item.title}</h3>
        {item.from && item.to ? (
          <p className="text-sm tabular-nums">
            <span className="text-slate-600">{item.from}</span>
            <span aria-hidden="true"> → </span>
            <span className="sr-only"> {t('changes.to')} </span>
            <span className="font-semibold text-green-800">{item.to}</span>
          </p>
        ) : null}
        <p>
          <span className="font-semibold">{t('changes.gain')}</span> {item.gain}
        </p>
        <p className="text-sm text-slate-600">{formatDate(item.at, i18n.language, timeZone)}</p>
      </div>
    </article>
  );
}
